import React, { useMemo } from "react";
import type { JSX } from "react";

import cn from "classnames";

import { mapButtonInlineViewToLoaderView } from "./constants/views";
import { Box, boxForwardRef } from "../../helpers/common/Box/Box";
import { useMouseDownAnimation } from "../../hooks/useMouseDownAnimation";
import { getChildrenRecord } from "../../utils/react";
import { getLoaderProps } from "../../utils/ui/getLoaderProps";
import { UniBoxAdditional } from "../UniBox";

import type { ButtonInlineProps, ButtonInlineState } from "./types";
import type { BoxComponentWithDefault } from "../../helpers/common/Box/Box";

import classes from "./ButtonInline.styl";

const DISABLED_STATES: ButtonInlineState[] = ["disabled", "loading"];
export const BUTTON_LABEL_ERROR =
    "В ButtonInline не был передан ни текст, ни aria-label";
const BUTTON_INLINE_DISPLAY_NAME = "ButtonInline";

/**
 * Базовый компонент, при нажатии на который пользователь может совершить действие. Служит для передачи свойств кнопки тексту или иконке.
 *
 * В качестве основного контента передаётся Label, Icon. Могут отображаться, а могут быть скрыты. В минимальной комплектации ButtonInline содержит или Label или Icon.
 *
 * В качестве `as` ожидаем, что будет передан 'button', 'a' или компонент, реализующий функцию навигации (например, `RoutingLink` на большом вебе).
 * Более подробную информацию можно найти [тут](/docs/документация-разработчикам-дс-инструкция-по-разработке-полиморфных-компонентов--docs).
 *
 * <a href="https://www.figma.com/design/LYZAXE33pUg2cSL5mmUHYq/%F0%9F%8D%91-Buttons-%26-Tabs-%26-Chips?m=auto&node-id=2-262157&t=3ryRFv5yEABAv4kK-1">Спека</a>
 * @constructor
 */
export const ButtonInline = boxForwardRef<
    BoxComponentWithDefault<"button", ButtonInlineProps>
>(
    (
        {
            as = "button",
            customColorsStyles,
            counterSize = "8",
            type = "button",
            view = "primary",
            state = "enabled",
            size,
            children,
            "aria-label": ariaLabel,
            "data-tsid": tsid,
            ...props
        },
        ref,
    ): JSX.Element => {
        const isDisabled = useMemo(
            () => DISABLED_STATES.includes(state),
            [state],
        );
        const { clicked, mousedownHandler } = useMouseDownAnimation();
        const isLoading = state === "loading";
        const realCounterSize =
            size === "24" || size === "32" ? counterSize : "8";
        const assetView = mapButtonInlineViewToLoaderView[view];

        const { Label, LeftIcon, RightIcon, Loader, Counter } = useMemo(
            () =>
                getChildrenRecord(children, "ButtonInline", {
                    Label: {
                        props: {
                            size,
                        },
                        optionalRequired: !children,
                    },
                    LeftIcon: {
                        props: {
                            size,
                        },
                        optionalRequired: !children,
                    },
                    RightIcon: {
                        props: {
                            size,
                        },
                        optionalRequired: !children,
                    },
                    Loader: {
                        props: getLoaderProps({
                            size,
                            view: assetView,
                            customColorsStyles:
                                classes["loader-custom-color-styles"],
                        }),
                        required: isLoading,
                    },
                    Counter: {
                        props: {
                            size: realCounterSize,
                        },
                    },
                    Unknown: {
                        skipElement: false,
                    },
                }),
            [children, realCounterSize, isLoading, size, assetView],
        );

        if (!Label && !ariaLabel) {
            throw new Error(BUTTON_LABEL_ERROR);
        }

        const buttonInlineClassName = cn(
            classes["button-inline"],
            classes[`__size-${size}`],
            {
                [classes["__state-disabled"]]: isDisabled,
            },
            {
                [classes["__state-loading"]]: isLoading,
            },
            {
                [classes["__clicked"]]: clicked,
            },
        );
        const wrapperClassName = cn(
            classes["button-inline-wrapper"],
            classes[`__size-${size}`],
            classes[`__view-${view}`],
            customColorsStyles,
        );

        const counterClassName = cn(
            classes["button-inline__counter"],
            classes[`__size-${realCounterSize}`],
        );

        return (
            <div className={wrapperClassName}>
                {isLoading && (
                    <span className={classes["button-inline__loader"]}>
                        {Loader}
                    </span>
                )}
                <Box
                    as={as}
                    {...props}
                    className={buttonInlineClassName}
                    type={type}
                    disabled={isDisabled}
                    onMouseDown={mousedownHandler}
                    aria-label={ariaLabel}
                    data-tsid={tsid}
                    data-uikit={BUTTON_INLINE_DISPLAY_NAME}
                    ref={ref}
                >
                    <div className={classes["button-inline__content"]}>
                        {LeftIcon}
                        {Label}
                        {RightIcon}
                    </div>
                    {Counter && (
                        <UniBoxAdditional>
                            <UniBoxAdditional.SlotTopRight
                                customVarsStyles={counterClassName}
                            >
                                {Counter}
                            </UniBoxAdditional.SlotTopRight>
                        </UniBoxAdditional>
                    )}
                </Box>
                <div className={classes["button-inline-outline"]} />
            </div>
        );
    },
);

ButtonInline.displayName = BUTTON_INLINE_DISPLAY_NAME;

export default ButtonInline;
